<template>
  <div class="project-list-item">
    <div class="project-list-item__summary">
      <div class="meta">
        <div class="logo">
          <router-link :to="default_view">
            <img v-if="project.logo !== 'http://placehold.it/150x150?text=Vispera'"
                 :src="project.logo">
            <span v-else class="like-logo">{{ project.name[0] }}</span>
          </router-link>
        </div>

        <div class="description">
          <router-link :to="default_view">
            <span>{{ project.display_name }}</span>
          </router-link>
          <time>{{ normalizeDate(project.last_visit.started_at) }}</time>
        </div>
      </div>

      <ProjectDashboards :project="project"
                         class="horizontal flat dashboard-cntrl"
                         :class="{'display-dashboards-mobile': showDetail}" />

      <el-button v-if="expandable"
                 class="expand-button"
                 :icon="getIcon"
                 @click="toggleDetail" />
    </div>

    <transition name="expand">
      <div v-if="showDetail" class="detail-container">
        <el-row :gutter="10" class="project-list-item__detail-list">
          <el-col :xs="12"
                  :sm="12"
                  :md="5"
                  :lg="4"
                  class="detail-item up-to-date">
            <div class="title">
              <b>{{ $t('Total Visits') }}</b><b><span>{{ project.counts.total_visits }}</span></b>
            </div>
            <div class="title">
              <b>{{ $t('Reported') }}</b><b><span>{{ project.counts.reported_visits }}</span></b>
            </div>
            <div class="title">
              <b>{{ $t('Discarded') }}</b><b><span>{{ project.counts.discarded_visits }}</span></b>
            </div>
          </el-col>
          <el-col :xs="12"
                  :sm="12"
                  :md="19"
                  :lg="20"
                  class="detail-horizontal-stats">
            <div class="detail-stats-item">
              <label class="detail-stats-item-title">{{ $t('SKU') }}</label>
              <span class="detail-stats-item-count">{{ project.counts.skus }}</span>
            </div>

            <div class="detail-stats-item">
              <label class="detail-stats-item-title">{{ $t('STORES') }}</label>
              <span class="detail-stats-item-count">{{ project.counts.stores }}</span>
            </div>

            <div class="detail-stats-item">
              <label class="detail-stats-item-title">{{ $t('VISITS') }}</label>
              <span class="detail-stats-item-count">{{ project.counts.total_visits }}</span>
            </div>

            <div class="detail-stats-item">
              <label class="detail-stats-item-title">{{ $t('AUDITORS') }}</label>
              <span class="detail-stats-item-count">{{ project.counts.auditors }}</span>
            </div>

            <div class="detail-stats-item">
              <label class="detail-stats-item-title">{{ $t('TEAMS') }}</label>
              <span class="detail-stats-item-count">{{ project.counts.teams }}</span>
            </div>
          </el-col>
        </el-row>
      </div>
    </transition>
  </div>
</template>

<script>
import ProjectDashboards from './ProjectDashboards';

export default {
  components: {
    ProjectDashboards
  },
  props: {
    project: {
      type: Object,
      default() {
        return {};
      }
    },
    expandable: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      showDetail: false
    };
  },
  computed: {
    default_view() {
      const { Breadcrumbs, project } = this;
      const { default_dashboard } = this.project.settings;

      this.onlyOneProjectCondition();

      if (default_dashboard) {
        return {
          name: Breadcrumbs[`${default_dashboard}`],
          params: {
            projectId: project.id
          }
        };
      }

      return {
        name: Breadcrumbs.projectDetail,
        params: {
          projectId: project.id
        }
      };
    },
    getIcon() {
      return this.showDetail ? 'mdi mdi-chevron-up mdi-24px' : 'mdi mdi-chevron-down mdi-24px';
    }
  },
  methods: {
    toggleDetail() {
      this.showDetail = !this.showDetail;
    },
    onlyOneProjectCondition() {
      if (this.expandable === false) {
        this.showDetail = true;
      }
    }
  }
};
</script>

<style lang="scss">
$detail-height: 100px;

.project-list-item {
  margin-bottom: 10px;
  background-color: $lynxWhite;
  border-radius: 4px;
  transition-timing-function: cubic-bezier(0.19, 1, 0.22, 1);
  transition-duration: 0.3s;

  &:hover {
    background-color: $white;
    box-shadow: 0 2px 12px 0 rgba($black, 0.1);
    transform: scale(1.009);

    .dashboards {
      display: flex;
    }
  }

  &__summary {
    position: relative;
    z-index: 3;
    display: flex;
    flex-wrap: wrap;
    align-items: stretch;

    .dashboards {
      display: none;
      height: 80px;
      padding: 10px 15px;

      @include breakpoint(lg-max) {
        padding: 10px 0;
      }
    }

    .meta {
      display: flex;
      flex-grow: 1;
      align-items: center;
      height: 100px;

      .logo {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100px;
        height: 100%;
        padding: 0 20px;

        a {
          box-sizing: border-box;
          display: flex;
          flex: 1;
          align-items: center;
          align-self: stretch;
          justify-content: center;
          padding: 10px;
        }

        img {
          max-width: 100%;
          max-height: 100%;
        }

        .like-logo {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 60px;
          height: 60px;
          font-size: 33px;
          color: $darkGrey;
          text-transform: uppercase;
          background-color: $white;
          border-radius: 50%;
        }
      }

      .description {
        display: inline-flex;
        flex-direction: column;

        a {
          text-decoration: none;
        }

        span {
          font-size: 19px;
          font-weight: 600;
          color: $cavernous;
          letter-spacing: 0.1px;
        }

        time {
          font-size: 12px;
          font-weight: bold;
          line-height: 1.58;
          color: $tin;
          letter-spacing: 0.5px;
        }
      }
    }

    .expand-button {
      position: absolute;
      top: 0;
      right: 10px;
      bottom: 0;
      padding: 5px;
      margin: auto;
      background: transparent;
      border: 0;
    }
  }

  .detail-container {
    padding: 20px 50px 20px 20px;
    overflow: hidden;
  }

  &__detail-list {
    .chart-container {
      width: 100%;
    }

    .highcharts-background {
      fill: transparent;
    }

    .detail-item {
      display: flex;
      flex-direction: column;
      align-items: flex-end;
      justify-content: flex-end;
      padding: 10px 10px;

      &.up-to-date {
        .title {
          display: flex;
          justify-content: space-between;
          width: 100%;
          font-size: 14px;
          font-weight: normal;
          color: $dimGrey;
          text-transform: uppercase;
        }
      }
    }

    .detail-horizontal-stats {
      display: flex;
      flex-direction: row;
      align-items: flex-end;
      justify-content: flex-end;

      .detail-stats-item {
        display: flex;
        flex-direction: column;
        align-items: flex-end;
        margin: 0 35px;

        &:last-of-type {
          margin: 0 10px 0 35px;
        }

        &-title {
          font-size: 14px;
          font-weight: bold;
          color: $dimGrey;
        }

        &-count {
          font-size: 36px;
          color: $dimGrey;
        }
      }
    }
  }

  @media (max-width: 1322px) {
    .menu-close {
      display: none;
    }

    .detail-container {
      .project-list-item__detail-list {
        .detail-horizontal-stats {
          .detail-stats-item {
            margin: 0 20px;

            &:last-of-type {
              margin-right: 0;
            }
          }
        }
      }
    }

    &__summary {
      .meta {
        padding-left: 10px;

        .logo {
          display: none;
        }
      }

      .dashboards {
        .dashboard {
          padding: 0 7px;

          &:first-of-type {
            padding: 0 7px;
          }

          &:last-of-type {
            padding: 0 7px;
          }
        }
      }
    }
  }

  @include breakpoint(lg-max) {
    background-color: $white;
    box-shadow: 0 2px 12px 0 rgba($black, 0.1);

    &:hover {
      .dashboards {
        display: none;
      }
    }

    .detail-container {
      display: flex;
      flex-direction: column;
      align-items: center;
      padding: 20px;

      .project-list-item__detail-list {
        display: flex;
        flex-direction: row;
        width: 100%;
        height: fit-content;

        .detail-item {
          justify-content: flex-start;
          width: 100%;
          padding: 0;
        }

        .detail-horizontal-stats {
          display: block;
          flex-direction: column;
          justify-content: flex-start;
          width: 100%;
          border-left: 1px solid $baiSeWhite;

          .detail-stats-item {
            flex-direction: row;
            justify-content: space-between;
            margin: 0;

            &:last-of-type {
              margin: 0;
            }

            &-title {
              font-size: 14px;
            }

            &-count {
              font-size: 14px;
              font-weight: bold;
            }
          }
        }
      }
    }

    &__summary {
      flex-direction: column;
      height: auto;
      padding: 10px;

      .display-dashboards-mobile {
        display: flex !important;
      }

      .dashboards {
        display: none;
        flex-direction: column;
        height: auto;

        .dashboard {
          flex-direction: row;
          align-items: center;
          justify-content: flex-start;
          width: auto;
          padding: 10px;
          margin: 0;
          font-size: 15px;
          background: #fff;
          border: 0;
          border-bottom: 1px solid $baiSeWhite;

          &:first-of-type {
            padding: 10px;
            border: 0;
            border-bottom: 1px solid $baiSeWhite;
          }

          &:last-of-type {
            padding: 10px;
            margin: 0;
            border: 0;
          }

          i {
            margin-right: 10px;
            font-size: 16px;
          }

          svg {
            width: 20px !important;
            height: 22px !important;
            margin-right: 7px;
            margin-left: -2px;
          }
        }
      }

      .meta {
        height: auto;
        padding: 0;

        .logo {
          display: block;
          width: 50px;
          padding: 0;
          margin-right: 10px;

          .like-logo {
            display: flex;
            width: 30px;
            height: 30px;
            font-size: 13px;
            background-color: $baiSeWhite;
          }
        }

        .description {
          span {
            font-size: 14px;
          }

          time {
            font-size: 10px;
          }
        }
      }

      .expand-button {
        position: absolute;
        top: 0;
        right: 0;
        bottom: inherit;
        padding: 15px;
      }
    }
  }

  @include breakpoint(sm-max) {
    .detail-container {
      .project-list-item__detail-list {
        flex-direction: column;

        .detail-horizontal-stats {
          border: none;
        }
      }
    }
  }
}

@keyframes expand {
  from {
    height: 0;
  }

  to {
    height: $detail-height;
  }
}

.expand-enter-active {
  animation-name: expand;
  animation-duration: 0.3s;
  animation-timing-function: cubic-bezier(0.175, 0.885, 0.32, 1.275);

  * {
    animation-name: fadeIn;
    animation-duration: 0.9s;
  }
}

.expand-leave-active {
  animation: expand;
  animation-duration: 0.3s;
  animation-timing-function: cubic-bezier(0.175, 0.885, 0.32, 1.275);
  animation-direction: reverse;

  * {
    animation-name: fadeOut;
    animation-duration: 0.9s;
  }
}
</style>
